import MainLayout from "@/components/layout/admin/MainLayout";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";

const DEFAULT_MAX_DATE_RANGE_DAYS = 31;
const TX_TYPE_EXPENSE = 2;
const TX_TYPE_ASSET = 3;
const TX_OUTFLOW_TYPES = [TX_TYPE_EXPENSE, TX_TYPE_ASSET];

export default {
  components: {
    MainLayout,
    Loading,
    flatPickr
  },
  data() {
    return {
      isFilterOpen: false,
      isLoading: false,
      maxRangeDays: DEFAULT_MAX_DATE_RANGE_DAYS,
      searchData: {
        from_date: moment().format("YYYY-MM-DD"),
        to_date: moment().format("YYYY-MM-DD")
      },
      searchError: "",
      reportData: {
        payment_methods: [],
        transactions: []
      }
    };
  },
  computed: {
    /**
     * Flatpickr config for both date pickers (no academic year restriction).
     */
    dateConfig() {
      return {
        dateFormat: "Y-m-d",
        altFormat: "d/m/Y",
        allowInput: false
      };
    },
    /**
     * Normalized payment method columns derived from the API response.
     * Pre-computed so the template does not recalculate number casts per render.
     */
    paymentMethodColumns() {
      const list =
        (this.reportData && this.reportData.payment_methods) || [];
      return list.map((pm) => ({
        id: pm.payment_method_id,
        name: pm.payment_method,
        beginningBalance: this.toNumber(pm.beginning_balance),
        totalIn: this.toNumber(pm.total_in),
        totalOut: this.toNumber(pm.total_out),
        endingBalance: this.toNumber(pm.ending_balance)
      }));
    },
    /**
     * Beginning Balance row (one entry per payment method).
     */
    beginningBalances() {
      return this.paymentMethodColumns.map((pm) => pm.beginningBalance);
    },
    /**
     * Ending Balance row (one entry per payment method).
     */
    endingBalances() {
      return this.paymentMethodColumns.map((pm) => pm.endingBalance);
    },
    /**
     * Normalized transaction rows for the table body.
     * Pre-computed so the template does not re-cast / re-derive per render.
     */
    transactions() {
      const list = (this.reportData && this.reportData.transactions) || [];
      return list.map((tx) => {
        const amount = this.toNumber(tx.amount);
        const type = Number(tx.type);
        const isExpense = TX_OUTFLOW_TYPES.includes(type);
        return {
          id: tx.id,
          paymentDate: tx.payment_date,
          invoiceNumber: tx.invoice_number,
          title: tx.title,
          amount: amount,
          isExpense: isExpense,
          displayAmount: isExpense ? -Math.abs(amount) : amount,
          paymentMethodId: Number(tx.payment_method_id)
        };
      });
    },
    /**
     * True when the report has at least one payment method to render.
     */
    hasReportData() {
      return this.paymentMethodColumns.length > 0;
    }
  },
  methods: {
    /**
     * Cast a value to a finite number, defaulting to 0.
     */
    toNumber(value) {
      const n = parseFloat(value);
      return isNaN(n) ? 0 : n;
    },
    /**
     * Format a number for display. Returns "—" for zero / falsy values to
     * keep the table compact when no transaction exists in that column.
     */
    formatNumber(value) {
      const n = this.toNumber(value);
      if (!n) return "—";
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    /**
     * Format a signed number. Expense values are already negated in the
     * displayAmount field, so this just renders them with a leading minus.
     */
    formatSignedNumber(value) {
      const n = this.toNumber(value);
      if (!n) return "—";
      const sign = n < 0 ? "-" : "";
      const absStr = Math.abs(n).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return sign + absStr;
    },
    /**
     * Look up the amount to display in a given payment-method column for a
     * given transaction. Returns null if the transaction does not affect
     * that payment method (so the template renders "—").
     */
    amountForColumn(tx, pmId) {
      if (!tx || tx.paymentMethodId !== pmId) return null;
      return tx.displayAmount;
    },
    /**
     * Validate the search form before submitting the API request.
     */
    validateDateRange() {
      const from = this.searchData.from_date;
      const to = this.searchData.to_date;
      if (!from || !to) {
        this.searchError = "Please select both From Date and To Date.";
        return false;
      }
      const fromMoment = moment(from, "YYYY-MM-DD", true);
      const toMoment = moment(to, "YYYY-MM-DD", true);
      if (!fromMoment.isValid() || !toMoment.isValid()) {
        this.searchError = "Please enter valid dates.";
        return false;
      }
      if (fromMoment.isAfter(toMoment)) {
        this.searchError = "From Date cannot be after To Date.";
        return false;
      }
      const days = toMoment.diff(fromMoment, "days") + 1;
      if (days > this.maxRangeDays) {
        this.searchError =
          "Date range cannot exceed " +
          this.maxRangeDays +
          " days. Selected range is " +
          days +
          " days.";
        return false;
      }
      this.searchError = "";
      return true;
    },
    reset() {
      this.searchData.from_date = moment().format("YYYY-MM-DD");
      this.searchData.to_date = moment().format("YYYY-MM-DD");
      this.searchError = "";
      this.search();
    },
    search() {
      if (!this.validateDateRange()) return;
      this.isLoading = true;
      this.reportData = { payment_methods: [], transactions: [] };
      axios
        .get("cashflow/daily", {
          params: {
            from_date: this.searchData.from_date,
            to_date: this.searchData.to_date
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data) {
            if (response.data.range_days_limit) {
              this.maxRangeDays = Number(response.data.range_days_limit);
            }
            this.reportData = {
              payment_methods: response.data.payment_methods || [],
              transactions: response.data.transactions || []
            };
          }
        })
        .catch((error) => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          console.error(error);
        });
    }
  },
  mounted() {
    this.search();
  }
};
